import * as React from 'react';
import { createPortal } from 'react-dom';

/**
 * Krok UXR1 — hlavicka stranky podle mockupu `01-dnesek.html`:
 * nadpis, podtitul a JEDNO primarni tlacitko vpravo, prilepene nad obsahem.
 *
 * V predloze je hlavicka soucast `<main>` kazde obrazovky; tady ji stranka
 * vykresli taky sama (`<PageHead …/>` jako prvni prvek), ale shell ji pres
 * portal umisti do sveho slotu. Duvod: nadpis stranky se na mobilu objevuje
 * v horni liste a nabidka „⋯" taky — bez spolecneho mista by to kazda
 * obrazovka resila po svem.
 *
 * Krok UXF4 bod 2: primarni akce uz se na mobilu NEPRENASI do lepene listy
 * nad spodni navigaci (nalez N10) — zustava v hlavicce stranky a posouva se
 * s obsahem, aby u spodni hrany byla jen JEDNA pevna lista.
 *
 * Mimo shell (unit testy routy) se hlavicka vykresli na miste — zadna
 * stranka tak nezmizi jen proto, ze kolem ni neni shell.
 */

export interface PageHeadSlots {
  /** Kam patri hlavicka stranky (uvnitr posuvne oblasti, `display: contents`). */
  readonly headSlot: HTMLElement | null;
  /**
   * Krok UXR2: kam patri nabidka „⋯" na mobilu. Mockup `m-03-detail-zakazky.html`
   * ji ma v HORNI LISTE vedle nazvu obrazovky — hlavicka stranky je na mobilu
   * schovana a primarni akce plni plovouci listu dole, takze bez tohoto slotu
   * by se k „⋯" na telefonu nikdo nedostal.
   */
  readonly menuSlot: HTMLElement | null;
  /**
   * Krok FIX6 bod 5: kam patri zpetna strelka „‹" na mobilu. Mockup
   * `m-03-detail-zakazky.html` ji ma v HORNI LISTE pred nazvem obrazovky a
   * komentar v `admin-v2.css` u skryteho `.rh-a2-crumb` to uz tvrdil
   * („Drobecky nese zpetna strelka v horni liste") — jen ten prvek nikdy
   * neexistoval, takze z detailu zakazky nevedla na telefonu cesta zpet
   * jinak nez spodnim menu (nalez ze ziveho overeni 2. 10.).
   */
  readonly backSlot: HTMLElement | null;
  /** Nadpis aktualni stranky — horni lista ho na mobilu ukazuje misto hledani. */
  readonly setTitle: (title: string | null) => void;
}

const PageHeadContext = React.createContext<PageHeadSlots | null>(null);

export const PageHeadProvider = PageHeadContext.Provider;

/** Mobilni rozvrzeni shellu (shoda s breakpointem 900 px v `admin-v2.css`). */
const MOBILE_QUERY = '(max-width: 900px)';

/**
 * `true` na mobilnim rozvrzeni. Primarni akce se vykresluje jen JEDNOU —
 * bud v hlavicce, nebo ve spodni liste. Dve kopie v DOM (jedna schovana
 * CSS) by znamenaly dve stejna `data-testid` a dvojity tab-stop.
 */
export function useIsMobileLayout(): boolean {
  const subscribe = React.useCallback((onChange: () => void) => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => undefined;
    const mql = window.matchMedia(MOBILE_QUERY);
    // `addEventListener` chybi jak starsim prohlizecum, tak nahradam
    // `matchMedia` v testech — bez teto kontroly by na nich spadl cely render.
    if (typeof mql.addEventListener !== 'function') return () => undefined;
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  const getSnapshot = React.useCallback(
    () =>
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia(MOBILE_QUERY).matches
        : false,
    [],
  );
  return React.useSyncExternalStore(subscribe, getSnapshot, () => false);
}

export interface PageHeadProps {
  readonly title: string;
  readonly subtitle?: React.ReactNode;
  /** Jedno primarni tlacitko vpravo (mockup: „+ Nová zakázka"). */
  readonly action?: React.ReactNode;
  /**
   * Krok UXR5 bod 6 — PATRI NADPIS NA MOBILU DO HORNI LISTY?
   *
   * Vychozi `true` (chovani od UXR1). Dnesek ma `false`: pozdrav nese tmavy
   * pas obrazovky (predloha `Mobil.dc.html`) a v liste by stal podruhe.
   * `h1` obrazovky zustava v DOM pro ctecku, jen uz se nekopiruje nahoru.
   */
  readonly mobileTitle?: boolean;
  /**
   * Krok UXR2 — drobecky nad nadpisem (mockup `03-detail-zakazky.html`:
   * „Zakázky › Úmyslovice"). Na mobilu je CSS skryva, protoze tam vede zpet
   * spodni menu a nadpis obrazovky je v horni liste.
   */
  readonly breadcrumb?: React.ReactNode;
  /**
   * Krok FIX6 bod 5 — zpetna strelka „‹" pro MOBIL (mockup
   * `m-03-detail-zakazky.html`). Na desktopu vede zpet drobecek, takze se
   * nevykresli vubec; na mobilu ji shell umisti do horni listy pred nazev.
   */
  readonly back?: React.ReactNode;
  /**
   * Krok UXR2 — nabidka „⋯" za primarnim tlacitkem. Na mobilu se presouva do
   * horni listy (`menuSlot`), ne do plovouci listy: tam patri JEDNA primarni
   * akce (mockup `m-03-detail-zakazky.html`).
   */
  readonly menu?: React.ReactNode;
  /**
   * `data-testid` nadpisu. Krok UXR2: obrazovky, ktere svuj nadpis mely drive
   * ve vlastni hlavicce (Smlouvy — `contracts-head-title`), ho presunuly sem.
   * Testid jde s nim, aby odkazy na nej (e2e UXS1) mirily na tentyz nadpis.
   */
  readonly titleTestId?: string;
  /**
   * Krok UXR3 — hlavicka ma DVE podoby, protoze predloha UX v3 je ma taky:
   *  - `band` (vychozi, `Zakazka.dc.html`): bily pas pres celou sirku,
   *  - `plain` (`Main.dc.html`): bez pasu, nadpis stoji na plose. Dnesek
   *    zacina pozdravem a tremi cisly, zadny bily pruh nad nimi neni.
   */
  readonly variant?: 'band' | 'plain';
  /**
   * Krok UXR3 — odznak VEDLE nadpisu (predloha `Zakazka.dc.html`:
   * „Hrabanov, 3 parcely" + pilulka „Pozemek · prodej"). Nemuze byt soucast
   * `title`: nadpis je `string`, protoze ho shell ukazuje i v mobilni liste.
   */
  readonly titleBadge?: React.ReactNode;
  /**
   * Krok UXR3 — co patri DO bileho pasu pod nadpis: prouzek fazi a lista
   * zalozek (predloha `Zakazka.dc.html`). Dokud to kreslila stranka pod
   * hlavickou, lezely zalozky na plose a pas pod nimi koncil uprostred.
   */
  readonly below?: React.ReactNode;
}

export function PageHead({
  title,
  subtitle,
  action,
  mobileTitle = true,
  breadcrumb,
  back,
  menu,
  titleTestId,
  variant = 'band',
  titleBadge,
  below,
}: PageHeadProps): React.JSX.Element {
  const slots = React.useContext(PageHeadContext);
  const setTitle = slots?.setTitle;
  const isMobile = useIsMobileLayout();

  // Krok UXR5 bod 6: obrazovka, ktera si nadpis na mobilu kresli sama, ho do
  // horni listy neposila — jinak by stal dvakrat.
  const barTitle = mobileTitle || !isMobile ? title : null;
  React.useEffect(() => {
    if (setTitle === undefined) return;
    setTitle(barTitle);
    return () => setTitle(null);
  }, [setTitle, barTitle]);

  /**
   * Krok UXF4 bod 2 (nalez N10 reauditu GPT 10. 10. 2026) — PRIMARNI AKCE
   * ZUSTAVA V HLAVICCE STRANKY I NA MOBILU. Do UXF4 se na 390 px prenasela
   * do lepene listy `.rh-a2-mbar` nad spodni navigaci, takze na pracovnich
   * seznamech staly u spodni hrany DVE pevne listy nad sebou a ukrajovaly
   * z 844 px. Listu i jeji slot shell zrusil; akce se posouva s obsahem.
   */
  const inlineAction = action !== undefined;
  const inlineMenu = menu !== undefined && !(isMobile && slots?.menuSlot != null);

  const head = (
    <div
      className={`rh-a2-pagehead${variant === 'plain' ? ' rh-a2-pagehead--plain' : ''}`}
      data-testid="page-head"
    >
      {breadcrumb !== undefined ? <div className="rh-a2-crumb">{breadcrumb}</div> : null}
      <div className="rh-a2-pagehead__top">
        <div>
          {titleBadge === undefined ? (
            <h1 className="rh-a2-pagehead__title" {...(titleTestId !== undefined ? { 'data-testid': titleTestId } : {})}>
              {title}
            </h1>
          ) : (
            <div className="rh-a2-chead__title">
              <h1 className="rh-a2-pagehead__title" {...(titleTestId !== undefined ? { 'data-testid': titleTestId } : {})}>
                {title}
              </h1>
              {titleBadge}
            </div>
          )}
          {subtitle !== undefined ? <div className="rh-a2-pagehead__sub">{subtitle}</div> : null}
        </div>
        {inlineAction || inlineMenu ? (
          <div className="rh-a2-pagehead__actions">
            {inlineAction ? action : null}
            {inlineMenu ? menu : null}
          </div>
        ) : null}
      </div>
      {below !== undefined ? <div className="rh-a2-pagehead__below">{below}</div> : null}
    </div>
  );

  return (
    <>
      {slots?.headSlot != null ? createPortal(head, slots.headSlot) : head}
      {!inlineMenu && menu !== undefined && slots?.menuSlot != null
        ? createPortal(menu, slots.menuSlot)
        : null}
      {/* Zpetna strelka je POUZE mobilni — na desktopu ji zastupuje drobecek. */}
      {isMobile && back !== undefined && slots?.backSlot != null
        ? createPortal(back, slots.backSlot)
        : null}
    </>
  );
}
